import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Product } from '@spartacus/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderEntry } from '@spartacus/cart/base/root';
import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import { TranslatePipe } from '@ngx-translate/core';

import {
  BrandsEnum,
  IconSizeEnum, IToaster,
  SdButtonComponent,
  SdIconComponent, SdListComponent,
  SdModalDefaultComponent,
  StatusEnum
} from '@seb/web-design-system/core';
import { Bundle } from '../../../../models';
import { ExternalLogisticianEnum } from '../../../../enums';
import { CartFacadeService, LoaderService, ToasterService, TrackingService } from '../../../../services';

@Component({
  selector: 'wc-compatibility-cart-modal',
  templateUrl: './compatibility-cart-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    SdListComponent,
    SdIconComponent,
    SdButtonComponent,
    TranslatePipe
  ]
})
export class CompatibilityCartModalComponent implements OnInit, OnDestroy {
  isConfirmModalOpened = false;
  cartId: string;
  cartEntries: OrderEntry[];
  qty = 1;
  currentProduct: Product;
  currentBundle: Bundle;
  isPFS: boolean;
  messageForfait = 'cart.modal.compatibilityCart.forfait.cartIsForfait';
  iconSize = IconSizeEnum;

  @Input()
  brand: BrandsEnum;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly cartFacadeService: CartFacadeService,
    private readonly multiCartService: MultiCartService,
    private readonly activeCartService: ActiveCartService,
    private readonly toasterService: ToasterService,
    private readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly trackingService: TrackingService
  ) {}

  ngOnInit(): void {
    this.activeCartService
      .getActiveCartId()
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeCartId) => {
        this.cartId = activeCartId;
      });

    this.activeCartService.getEntries().subscribe((entries) => {
      this.cartEntries = entries;
    });

    this.cartFacadeService.toggleCart$.pipe(takeUntil(this.destroy$)).subscribe((cart) => {
      this.messageForfait = cart.forfait;
      this.isPFS = cart.product?.externalLogistician === ExternalLogisticianEnum.PFS || !!cart.bundle;
      this.isConfirmModalOpened = true;
      this.currentProduct = cart.product;
      this.currentBundle = cart.bundle;
      this.qty = cart.quantity;
      this.cdRef.markForCheck();
      this.loaderService.hideGlobalLoader();
    });
  }

  createNewCart(): void {
    if (this.currentProduct) {
      const removedProductEvents = [];
      this.cartEntries.map((entry) => {
        removedProductEvents.push(this.trackingService.productToEventProduct(entry.product, undefined, entry.quantity));
      });
      const addedProductEvent = this.trackingService.productToEventProduct(this.currentProduct, undefined, this.qty);
      this.cartFacadeService.forceEmptyCart(this.currentProduct.code, this.qty).subscribe(
        () => {
          this.isConfirmModalOpened = false;
          this.multiCartService.reloadCart(this.cartId);
          this.cdRef.markForCheck();
          this.trackingService.sendEvent('clickRemoveFromCart', removedProductEvents);
          this.trackingService.sendEvent('clickAddToCart', addedProductEvent);
        },
        (error) => {
          this.isConfirmModalOpened = false;
          this.cdRef.markForCheck();
          const toaster: IToaster = {
            status: StatusEnum.error,
            title: this.mapErrorMessageToPropertyKey(error.error?.errors?.[0]?.message)
          };
          this.toasterService.sendToasterEvent(toaster);
        }
      );
    }
    if (this.currentBundle) {
      this.cartFacadeService.addBundleToCart(this.currentBundle, this.qty, true).subscribe(
        () => {
          this.isConfirmModalOpened = false;
          this.cdRef.markForCheck();
        },
        (error) => {
          this.isConfirmModalOpened = false;
          this.cdRef.markForCheck();
          const toaster: IToaster = {
            status: StatusEnum.error,
            title: 'cart.modal.compatibilityCart.errors.title',
            description: this.mapErrorMessageToPropertyKey(error.error?.errors?.[0]?.message)
          };
          this.toasterService.sendToasterEvent(toaster);
        }
      );
    }
  }

  goToCart(): void {
    this.isConfirmModalOpened = false;
    this.router.navigateByUrl('/cart');
  }

  continue(): void {
    this.isConfirmModalOpened = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private mapErrorMessageToPropertyKey(message: string) {
    switch (message) {
      case 'There is no price available for this product.':
        return 'cart.modal.compatibilityCart.errors.noPrice';
      case 'There is no vendor available for this product.':
        return 'cart.modal.compatibilityCart.errors.noVendor';
      case 'This product is not on sale.':
        return 'cart.modal.compatibilityCart.errors.notOnSale';
      case 'This product is unavailable':
        return 'cart.modal.compatibilityCart.errors.unavailable';
      default:
        return 'cart.modal.compatibilityCart.errors.global';
    }
  }
}
